import { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Button, Dropdown, Flex, Input, Space, Switch } from 'antd';
import { LeftOutlined, MenuOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { ExportButton } from '../ExportButton/ExportButton';
import { ImportButton } from '../importButton';
import { Datatable } from '../DataTable/Datatable';
import { PageHeaderCustom } from '../PageHeader/PageHeader';
import { ContentCustom } from '../ContentCustom/ContentCustom';
import { AddIcon } from '../../utils/constants/customIcons';

const { Search } = Input;

export const ListResource = ({
  resourceName,
  tradKey,
  dataToFetch,
  columns,
  customActionColumn,
  headers,
  children,
  populate,
  extraQuery,
  extraHeader,
  extraButtons,
  exportUrl,
  withArchiveButton,
  withCreateButton,
  withUploadButton,
  withPageHeader,
  withSearchBar,
  forceRefresh,
  resourceModelName,
  editAction,
  showAction,
  duplicateAction,
  printAction,
  deleteAction,
  onDoubleClickAction,
  scroll,
  expandable,
  path,
  rowKey,
  formatter,
  extraFilters,
  withCustomCreateButton,
  customCreateButtonAction,
  setResourceData,
  rowSelection,
  customPathName,
  customPageHeaderTitle,
  bottom_children,
  withoutImportButton,
  backIcon,
  withoutBreadCrumb
}) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const keyword = params.get('k');
  const pageSize = params.get('pS');
  const currentFilters = params.get('f');
  const currentSorter = params.get('s');
  const [searchValue, setSearchValue] = useState(keyword);
  const [archiveFilter, setArchiveFilter] = useState(false);

  const searchResource = (value) => {
    const pageSizeParam = pageSize ? `&pS=${pageSize}` : '';
    const sorterParam = currentSorter ? `&s=${currentSorter}` : '';
    const filterParam = currentFilters ? `&f=${currentFilters}` : '';
    const searchParam = value ? `&k=${value}` : '';
    const searchString = `?p=1${pageSizeParam}${sorterParam}${filterParam}${searchParam}`;
    navigate({
      pathname,
      search: searchString
    });
  };

  useEffect(() => {
    setSearchValue(null);
  }, [pathname]);

  useEffect(() => {
    if (keyword) setSearchValue(keyword);
    else setSearchValue(null);
  }, [keyword]);

  const menu = {
    items: [
      ...(headers
        ? [
            {
              key: 'export',
              label: (
                <ExportButton
                  dataName={resourceName}
                  headers={headers}
                  url={`/${exportUrl || resourceName}`}
                  fileName={`${resourceName}.csv`}
                  populate={populate}
                  extraQuery={extraQuery}
                  formatter={formatter}
                />
              )
            }
          ]
        : []),
      ...(withoutImportButton
        ? []
        : [
            {
              key: 'import',
              label: <ImportButton resourceName={resourceModelName} />
            }
          ])
    ]
  };

  return (
    <>
      {withPageHeader && (
        <PageHeaderCustom
          title={customPageHeaderTitle || t(`${tradKey || resourceName}.title`)}
          extra={extraHeader}
          customPathName={customPathName}
          backIcon={!backIcon || <LeftOutlined />}
          withoutBreadCrumb={withoutBreadCrumb}
        />
      )}
      <ContentCustom
        style={{ padding: '16px 20px', margin: '0 40px 16px', flex: 'none' }}
      >
        <Flex
          className="list-toolbar"
          justify={withSearchBar ? 'space-between' : 'end'}
          gap="small"
          align="center"
          wrap="wrap"
        >
          {withSearchBar && (
            <Search
              allowClear
              placeholder={t('placeholder.search')}
              defaultValue={searchValue}
              onSearch={(value) => searchResource(value)}
              style={{ minWidth: 190, maxWidth: 300 }}
            />
          )}
          {!withCreateButton && headers && (
            <Dropdown menu={menu}>
              <Button type="link">
                <MenuOutlined
                  style={{ fontSize: 16, color: 'var(--textColor)' }}
                />
              </Button>
            </Dropdown>
          )}
          {extraFilters}
          {withArchiveButton && (
            <Space>
              <Switch
                onChange={() => setArchiveFilter(!archiveFilter)}
                checkedChildren={t('buttons.archived')}
                unCheckedChildren={t('buttons.not_archived')}
              />
            </Space>
          )}

          {withCreateButton && (
            <Flex align="center">
              {extraButtons}
              <Link to={withCreateButton?.path || `${pathname}/create`}>
                <Button type="add">
                  {withCreateButton?.buttonText || `${t('buttons.create')}`}
                  &nbsp;
                  {withCreateButton?.buttonIcon || <AddIcon />}
                </Button>
              </Link>
              {withUploadButton && (
                <Dropdown menu={menu}>
                  <Button type="link">
                    <MenuOutlined
                      style={{ fontSize: 16, color: 'var(--textColor)' }}
                    />
                  </Button>
                </Dropdown>
              )}
            </Flex>
          )}
          {withCustomCreateButton && (
            <Flex align="center">
              {extraButtons}

              <Button type="add" onClick={customCreateButtonAction}>
                {`${t('buttons.create')}`}
                &nbsp;
                <AddIcon />
              </Button>
            </Flex>
          )}
        </Flex>
        {children && (
          <Flex gap="small" align="center">
            {children}
          </Flex>
        )}
      </ContentCustom>
      <ContentCustom style={{ padding: '0', overflow: 'hidden', flex: 'none' }}>
        <Datatable
          resourceName={dataToFetch || resourceName}
          columns={columns}
          customActionColumn={customActionColumn}
          extraQuery={extraQuery}
          populate={populate}
          forceRefresh={forceRefresh}
          editAction={editAction}
          showAction={showAction}
          duplicateAction={duplicateAction}
          printAction={printAction}
          deleteAction={deleteAction}
          onDoubleClickAction={onDoubleClickAction}
          scroll={scroll || { x: 1000 }}
          expandable={expandable}
          path={path}
          rowKey={rowKey}
          archiveFilter={archiveFilter}
          setResourceData={setResourceData}
          rowSelection={rowSelection}
          navigate={navigate}
        />
        {bottom_children && (
          <Flex gap="small" align="center" style={{ padding: '0 20px 20px' }}>
            {bottom_children}
          </Flex>
        )}
      </ContentCustom>
    </>
  );
};

ListResource.propTypes = {
  resourceName: PropTypes.string.isRequired,
  tradKey: PropTypes.string,
  dataToFetch: PropTypes.string,
  columns: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  customActionColumn: PropTypes.bool,
  headers: PropTypes.arrayOf(PropTypes.shape({})),
  extraQuery: PropTypes.string,
  extraHeader: PropTypes.element,
  extraButtons: PropTypes.element,
  exportUrl: PropTypes.string,
  populate: PropTypes.string,
  withArchiveButton: PropTypes.bool,
  withCreateButton: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.shape({
      path: PropTypes.string,
      buttonText: PropTypes.string,
      buttonIcon: PropTypes.element
    })
  ]),
  withUploadButton: PropTypes.bool,
  withPageHeader: PropTypes.bool,
  withSearchBar: PropTypes.bool,
  forceRefresh: PropTypes.bool,
  resourceModelName: PropTypes.string,
  editAction: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.shape({
      pathname: PropTypes.func
    })
  ]),
  duplicateAction: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.shape({
      pathname: PropTypes.func
    })
  ]),
  printAction: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.shape({
      pathname: PropTypes.func
    })
  ]),
  showAction: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.shape({
      pathname: PropTypes.func
    })
  ]),
  deleteAction: PropTypes.bool,
  onDoubleClickAction: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.shape({
      action: PropTypes.func
    })
  ]),
  scroll: PropTypes.shape({}),
  expandable: PropTypes.shape({}),
  path: PropTypes.string,
  rowKey: PropTypes.string,
  formatter: PropTypes.func,
  extraFilters: PropTypes.element,
  withCustomCreateButton: PropTypes.bool,
  customCreateButtonAction: PropTypes.func,
  setResourceData: PropTypes.func,
  rowSelection: PropTypes.shape({}),
  customPathName: PropTypes.string,
  customPageHeaderTitle: PropTypes.oneOfType([
    PropTypes.shape({}),
    PropTypes.string
  ]),
  bottom_children: PropTypes.element,
  withoutImportButton: PropTypes.bool,
  backIcon: PropTypes.bool,
  withoutBreadCrumb: PropTypes.bool
};

ListResource.defaultProps = {
  tradKey: null,
  headers: null,
  extraQuery: null,
  extraHeader: null,
  extraButtons: null,
  exportUrl: null,
  populate: null,
  customActionColumn: false,
  withArchiveButton: false,
  withCreateButton: true,
  withUploadButton: true,
  withSearchBar: true,
  withPageHeader: true,
  dataToFetch: null,
  forceRefresh: null,
  resourceModelName: null,
  editAction: true,
  showAction: true,
  duplicateAction: false,
  printAction: false,
  deleteAction: true,
  onDoubleClickAction: true,
  scroll: null,
  expandable: undefined,
  path: null,
  rowKey: '_id',
  formatter: undefined,
  extraFilters: null,
  withCustomCreateButton: false,
  customCreateButtonAction: () => {},
  setResourceData: () => {},
  rowSelection: undefined,
  customPathName: null,
  customPageHeaderTitle: null,
  bottom_children: null,
  withoutImportButton: false,
  backIcon: false,
  withoutBreadCrumb: false
};
